import { useEffect, useRef, type ReactElement, type ReactNode } from 'react';
import {
  Body,
  Box,
  Button,
  Checkbox,
  I,
  Input,
  Pill,
  Row,
  Select,
  Stack,
} from '@leanin/ui';
import type { SpaceKey } from '@leanin/ui';
import {
  MAX_PROMPT_CHARS,
  type IntakeQuestionType,
} from '@leanin/shared/intake';
import type { DraftQuestion } from './intake-builder-model';
import { QuestionEditor } from './QuestionEditor';
import styles from './join-questions.module.css';

type TypeOption = { readonly value: IntakeQuestionType; readonly label: string };

type Props = {
  readonly prompt: string;
  readonly typeLabel: string;
  readonly helper?: ReactNode;
  /** Dims the prompt for an available-but-not-added recommended row. */
  readonly muted?: boolean;
  readonly checked: boolean;
  readonly onToggleChecked: (checked: boolean) => void;
  readonly checkboxDisabled?: boolean;
  readonly checkboxLabel: string;
  /** Real drag handle from the Sortable primitive (draggable rows). */
  readonly dragHandle?: ReactElement;
  /** Render an inert six-dot placeholder for alignment (unselected rows). */
  readonly handlePlaceholder?: boolean;
  // --- in-place editing (real questions only) ---
  readonly question?: DraftQuestion;
  readonly editing?: boolean;
  readonly onEdit?: () => void;
  readonly editLabel?: string;
  readonly collapseLabel?: string;
  /** Label for the primary "Done" button that closes an open editor. */
  readonly doneLabel?: string;
  readonly promptPlaceholder?: string;
  readonly typeSelectLabel?: string;
  /** When true, the answer type is editable via a dropdown; else shown as a static pill. */
  readonly allowTypeChange?: boolean;
  readonly typeOptions?: readonly TypeOption[];
  readonly onPatch?: (patch: Partial<DraftQuestion>) => void;
  readonly onChangeType?: (type: IntakeQuestionType) => void;
  readonly errorMessage?: string | null;
  // --- remove ---
  readonly onRemove?: () => void;
  readonly removeLabel?: string;
  // --- styling ---
  readonly tinted?: boolean;
  readonly rounded?: boolean;
  readonly divider?: boolean;
  readonly padX?: SpaceKey;
  readonly padY?: SpaceKey;
  readonly accentPill?: boolean;
};

// Inert grip glyph matching the Sortable drag handle, for unselected recommended
// rows so they stay aligned with the draggable rows. Visually disabled.
function HandlePlaceholder() {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 44,
        color: 'var(--muted-foreground)',
        opacity: 0.35,
      }}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <circle cx="5" cy="4" r="1.5" />
        <circle cx="11" cy="4" r="1.5" />
        <circle cx="5" cy="8" r="1.5" />
        <circle cx="11" cy="8" r="1.5" />
        <circle cx="5" cy="12" r="1.5" />
        <circle cx="11" cy="12" r="1.5" />
      </svg>
    </span>
  );
}

// One question in the picker. Collapsed it is a compact row; editing it becomes
// the in-place editor — the SAME card, with the wording turning into a text
// input and the answer-type pill into a dropdown, and type-specific controls
// revealed beneath. The wording is never shown twice, and no separate editor
// section is appended below the row.
export function QuestionRow({
  prompt,
  typeLabel,
  helper,
  muted,
  checked,
  onToggleChecked,
  checkboxDisabled,
  checkboxLabel,
  dragHandle,
  handlePlaceholder,
  question,
  editing,
  onEdit,
  editLabel,
  collapseLabel,
  doneLabel,
  promptPlaceholder,
  typeSelectLabel,
  allowTypeChange,
  typeOptions,
  onPatch,
  onChangeType,
  errorMessage,
  onRemove,
  removeLabel,
  tinted,
  rounded,
  divider,
  padX = 'md',
  padY = 'sm',
  accentPill,
}: Props) {
  const isEditing = editing === true && question !== undefined;
  const handle = dragHandle ?? (handlePlaceholder ? <HandlePlaceholder /> : null);
  const wordingRef = useRef<HTMLButtonElement>(null);
  // A keyboard collapse (activating the pencil) unmounts the pencil, so focus
  // would fall to <body> and the leader would lose their place. Move it to the
  // row's collapsed wording trigger instead. Click-away collapses leave focus
  // where the pointer landed, so they don't set this flag.
  const restoreFocusOnCollapse = useRef(false);
  useEffect(() => {
    if (!isEditing && restoreFocusOnCollapse.current) {
      restoreFocusOnCollapse.current = false;
      wordingRef.current?.focus();
    }
  }, [isEditing]);

  // Same marker the applicant sees on required consent checkboxes — the
  // builder row mirrors it so a leader can spot required questions at a glance.
  const requiredMarker =
    question?.required === true ? (
      <span aria-hidden="true" style={{ color: 'var(--destructive)' }}>
        {' '}
        *
      </span>
    ) : null;

  // Shared side controls — identical element in both layouts below, so the
  // editing and collapsed branches can't drift apart.
  const checkboxEl = (
    <Checkbox
      checked={checked}
      onCheckedChange={(v) => onToggleChecked(v === true)}
      disabled={checkboxDisabled}
      aria-label={checkboxLabel}
    />
  );
  const typePill = (
    <Pill
      size="xs"
      style={{ alignSelf: 'center' }}
      {...(accentPill ? { tone: 'burgundy' as const } : {})}
    >
      {typeLabel}
    </Pill>
  );
  const editButton =
    onEdit !== undefined ? (
      <Button
        variant="ghost"
        size="sm"
        icon={I.edit}
        aria-label={isEditing ? collapseLabel : editLabel}
        onClick={() => {
          // Collapsing (from the editing state) arms focus-restore to the
          // wording trigger — but only when it will actually collapse. A row
          // held open by a validation error doesn't collapse now, so arming
          // would leave a stale flag that yanks focus later once it's fixed.
          if (isEditing && errorMessage == null) {
            restoreFocusOnCollapse.current = true;
          }
          onEdit();
        }}
      />
    ) : null;
  const removeButton = onRemove ? (
    <Button
      variant="ghost"
      size="sm"
      icon={I.x}
      aria-label={removeLabel}
      onClick={onRemove}
    />
  ) : null;

  const showCounter =
    question !== undefined && question.prompt.length > MAX_PROMPT_CHARS - 40;

  return (
    <Box
      {...(rounded ? { rounded: 'lg' } : {})}
      style={{
        ...(tinted ? { background: 'var(--light-poppy)' } : {}),
        ...(isEditing ? { background: 'var(--light-poppy)' } : {}),
        ...(divider ? { borderTop: '1px solid var(--border)' } : {}),
      }}
    >
      {isEditing && question !== undefined ? (
        <>
          {/* EVERY control shares the input's line — handle, checkbox,
              select/pill, pencil, remove — so the row's centering centers them
              on the input box itself. The helper/counter/error lines render
              BELOW the row (not in a shared cell) so they can never make one
              cell taller and push the controls off the input's midline. */}
          <Row align="center" gap="sm" px={padX} py={padY}>
            {handle}
            {checkboxEl}
            <Input
              autoFocus
              value={question.prompt}
              maxLength={MAX_PROMPT_CHARS}
              placeholder={promptPlaceholder}
              invalid={errorMessage != null}
              aria-label={promptPlaceholder}
              onChange={(e) => onPatch?.({ prompt: e.target.value })}
              style={{ flex: 1, minWidth: 0 }}
            />
            {allowTypeChange && typeOptions ? (
              <div style={{ width: 152, flexShrink: 0 }}>
                <Select
                  value={question.type}
                  aria-label={typeSelectLabel}
                  options={typeOptions.map((o) => ({ value: o.value, label: o.label }))}
                  onChange={(e) => onChangeType?.(e.target.value as IntakeQuestionType)}
                  style={{ width: '100%' }}
                />
              </div>
            ) : (
              typePill
            )}
            {editButton}
            {removeButton}
          </Row>
          <Box px={padX} pb={padY}>
            <Stack gap="md">
              {showCounter || errorMessage != null || helper != null ? (
                <Stack gap="xs">
                  {showCounter ? (
                    <Body size="sm" color="mute">
                      {question.prompt.length} / {MAX_PROMPT_CHARS}
                    </Body>
                  ) : null}
                  {errorMessage != null ? (
                    <Body size="sm" color="destructive">
                      {errorMessage}
                    </Body>
                  ) : null}
                  {helper}
                </Stack>
              ) : null}
              {onPatch ? (
                <QuestionEditor
                  question={question}
                  onPatch={onPatch}
                  requiredIndent="md"
                  footer={
                    onEdit !== undefined && doneLabel !== undefined ? (
                      <Button
                        variant="primary"
                        size="sm"
                        icon={I.check}
                        disabled={errorMessage != null}
                        onClick={() => {
                          restoreFocusOnCollapse.current = true;
                          onEdit();
                        }}
                      >
                        {doneLabel}
                      </Button>
                    ) : undefined
                  }
                />
              ) : null}
            </Stack>
          </Box>
        </>
      ) : (
        <Row align="center" gap="sm" px={padX} py={padY}>
          {handle}
          {checkboxEl}
          <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
            {onEdit !== undefined ? (
              // The collapsed wording is a click-to-edit trigger (Google-Forms
              // style) — its accessible name is the wording itself; the adjacent
              // pencil is the explicit "Edit" control. Both open the editor.
              <button
                ref={wordingRef}
                type="button"
                className={styles.promptTrigger}
                onClick={onEdit}
              >
                {prompt}
                {requiredMarker}
              </button>
            ) : (
              <Body color={muted ? 'mute' : undefined}>
                {prompt}
                {requiredMarker}
              </Body>
            )}
            {helper}
          </Stack>
          {typePill}
          {editButton}
          {removeButton}
        </Row>
      )}
    </Box>
  );
}
